<script setup lang="ts">
import FileViewer from '@/components/common/file-viewer.vue';
import SupplierInvoiceMetaData from '@/components/invoices/details/meta-data/supplier-invoice-meta-data.vue';
import SplitInvoices from '@/components/invoices/details/split-invoices.vue';
import ReviewersSelect from '@/components/project/reviewers-select.vue';
import SimpleTable from '@/components/tables/SimpleTable.vue';
import i18n from '@/i18n';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { entraAuthService } from '@/services/auth/entra-auth-service.ts';
import { supplierInvoiceService } from '@/services/invoices/supplier-invoice-service';
import { projectService } from '@/services/project/project-service';
import { PhPencilSimple } from '@phosphor-icons/vue';
import { hasPermission, models, pagePermissions, utils } from 'cesg-shared';
import type { RequiredPermission } from 'cesg-shared/dist/functions/auth/permission.js';
import {
  PermissionResource,
  SupplierInvoiceStatusType,
} from 'cesg-shared/dist/models/index.js';
import { computed, createApp, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import {
  getSupplierInvoiceColumns,
  sortSupplierInvoices,
  supplierInvoiceAmountKey,
} from '../supplier-invoce-table-common';
import SupplierInvoiceProjectChanger, {
  type SupplierInvoiceProjectChangerMode,
} from './meta-data/supplier-invoice-project-changer.vue';
import SupplierInvoiceReview from './supplier-invoice-review.vue';

const { t } = useI18n();
const route = useRoute();
const supplierInvoice = ref<models.SupplierInvoiceResponse>({
  id: '',
  rows: [],
  project: '',
});

const supplierInvoiceProjectId = ref<string>();

const invoiceReviewers = ref<models.FortnoxUserReviewStatus[]>([]);
const allUsers = ref<models.FortnoxUser[]>([]);
const invoiceReviewersRanking = ref<models.FortnoxUserReviewRanking[]>([]);
const updatedReviewers = ref<models.FortnoxUserReviewRanking[]>([]);
const invoiceFiles = ref<models.SupplierInvoiceFile[] | null>(null);
const invoicesFromSupplier = ref<models.SupplierInvoicesResponse[]>([]);

const showData = ref<boolean>(false);
const isEditing = ref<boolean>(false);

const supplierInvoiceColumns = getSupplierInvoiceColumns(t);

const currentUser: models.GetMeResponse | null = entraAuthService.me.value;

interface SplitInvoicesExposed {
  saveInvoice: () => Promise<boolean>;
}

const splitInvoicesRef = ref<SplitInvoicesExposed | null>(null);
const suppressProjectSideEffect = ref<boolean>(false);

function openPopup(files: models.SupplierInvoiceFile[]) {
  const popup = window.open('', 'FileViewerTab');

  if (!popup) {
    alert('Popup does not exist!');
    return;
  }

  popup.document.title = 'Faktura - Förhandsgranskning';
  const container = popup.document.createElement('div');
  popup.document.body.appendChild(container);

  createApp(FileViewer, { files }).use(i18n).mount(container);
}

const loadData = async () => {
  overlaySpinnerService.show();
  try {
    supplierInvoice.value = await supplierInvoiceService.get(
      utils.type.asType<string>(route.params.invoiceId),
    );

    invoiceReviewers.value = [];
    if (permittedTo.value?.read.reviewers || permittedTo.value?.read.review) {
      invoiceReviewers.value =
        await supplierInvoiceService.getSupplierInvoiceReviewers(
          utils.type.asType<string>(route.params.invoiceId),
        );
    }

    const files = await supplierInvoiceService.getFiles(
      utils.type.asType<string>(route.params.invoiceId),
    );

    invoiceReviewers.value = invoiceReviewers.value.sort(
      (a, b) => a.rank - b.rank,
    );
    // Re-index to a gap-free 0..n-1 sequence after sorting rather than
    // forwarding the stored rank, so the reviewer-update payload always
    // satisfies the server's continuous-from-zero validator even if the
    // persisted ranks are legacy/sparse.
    invoiceReviewersRanking.value = invoiceReviewers.value.map((r, i) => ({
      id: r.id,
      ranking: i,
    }));
    updatedReviewers.value = invoiceReviewersRanking.value;
    allUsers.value = await projectService.getAllReviewers();
    invoiceFiles.value = files.length > 0 ? files : null;
    supplierInvoiceProjectId.value = supplierInvoice.value.project;
  } finally {
    overlaySpinnerService.hide();
    showData.value = true;
  }
};

const loadInvoicesFromSupplier = async (
  projectId: string,
  filter?: {
    supplierOrganisationNumber?: string;
  },
) => {
  const invoices = await supplierInvoiceService.getAll(projectId, filter);

  const filteredInvoices = invoices.filter(
    (invoice) => invoice.id !== supplierInvoice.value.id,
  );

  invoicesFromSupplier.value = sortSupplierInvoices(filteredInvoices);
};

const openInvoice = async (id: number) => {
  const url = router.resolve({
    name: RouteName.InvoiceDetails,
    params: { id: route.params.id, invoiceId: id },
  }).href;
  window.open(url, '_blank');
};

function toggleEdit() {
  isEditing.value = !isEditing.value;
}

async function updateReviewers(reviewers: models.FortnoxUserReviewRanking[]) {
  updatedReviewers.value = reviewers;
}

async function submitUpdateReviewers() {
  overlaySpinnerService.show();
  try {
    await supplierInvoiceService.updateSupplierInvoiceReviewers(
      updatedReviewers.value,
      utils.type.asType<string>(route.params.invoiceId),
    );
    isEditing.value = false;
    await loadData();
  } finally {
    overlaySpinnerService.hide();
  }
}

const hasPermissionInvoice = computed(() => {
  return (
    permissionType: models.PermissionType | models.ReviewPermissionType,
  ): boolean => {
    if (!currentUser) return false;
    const permissionsRequired: RequiredPermission[] = [
      {
        resource: PermissionResource.INVOICE,
        permission: permissionType,
      },
    ];
    const currentProjectId = supplierInvoice.value.project;
    if (!currentProjectId) return false;

    return hasPermission(currentProjectId, permissionsRequired, currentUser);
  };
});

const reloadSupplierInvoiceDetails = async () => {
  await loadData();
  await loadInvoicesFromSupplier(supplierInvoice.value.project, {
    supplierOrganisationNumber:
      supplierInvoice.value.organisationNumber ?? undefined,
  });
};

watch(
  [
    () => String(route.params.id ?? ''),
    () => String(route.params.invoiceId ?? ''),
  ],
  async ([newProjectId, newInvoiceId], [oldProjectId, oldInvoiceId]) => {
    if (newProjectId === oldProjectId && newInvoiceId === oldInvoiceId) {
      return;
    }
    await reloadSupplierInvoiceDetails();
  },
  { immediate: true },
);

onBeforeRouteLeave((to, from, next) => {
  if (
    isEditing.value &&
    JSON.stringify(invoiceReviewersRanking.value) !==
      JSON.stringify(updatedReviewers.value)
  ) {
    const confirmLeave = window.confirm(t('unsavedChangesNotification'));

    if (confirmLeave) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});

async function onSaved(savedResponse: models.SupplierInvoiceResponse) {
  // When the save was triggered as part of a review action (attest/contest/park),
  // skip the project-change persistence and navigation — the review flow only
  // persists the split form and drives its own navigation to the invoice list.
  if (suppressProjectSideEffect.value) {
    return;
  }

  if (supplierInvoice.value.organisationNumber) {
    await loadInvoicesFromSupplier(savedResponse.project, {
      supplierOrganisationNumber: supplierInvoice.value.organisationNumber,
    });
  }

  if (
    !supplierInvoiceProjectId.value ||
    supplierInvoiceProjectId.value === supplierInvoice.value.project
  ) {
    return;
  }

  const previousProjectId = String(route.params.id ?? '');
  const previousInvoiceId = String(route.params.invoiceId ?? '');

  await supplierInvoiceService.updateSupplierInvoiceProject(
    supplierInvoice.value.id,
    {
      projectId: supplierInvoiceProjectId.value,
    },
  );

  await router.push({
    name: RouteName.Invoices,
    params: {
      id: route.params.id,
    },
  });

  // Fallback: if push resolves to the same params, trigger refresh manually.
  if (
    String(route.params.id ?? '') === previousProjectId &&
    String(route.params.invoiceId ?? '') === previousInvoiceId
  ) {
    await reloadSupplierInvoiceDetails();
  }
}

const permittedTo = computed(
  () => pagePermissions(supplierInvoice.value.project, currentUser)?.invoices,
);

// Editable if the user may edit comments or splits (mirrors the split form's own
// save-button visibility). When not editable, there is nothing to save.
const formIsEditable = computed(
  () =>
    !!permittedTo.value?.edit.comments ||
    (!!permittedTo.value?.edit.invoice &&
      supplierInvoice.value.status !== SupplierInvoiceStatusType.Voided),
);

// Called by the review child before approve/contest/park. Saves the split form
// (notes + splits) while suppressing the project-change side effect, and reports
// success so the review action can be aborted if the save fails.
async function saveFormBeforeReview(): Promise<boolean> {
  if (!splitInvoicesRef.value || !formIsEditable.value) {
    return true;
  }
  suppressProjectSideEffect.value = true;
  try {
    return await splitInvoicesRef.value.saveInvoice();
  } finally {
    suppressProjectSideEffect.value = false;
  }
}

const projectChangerMode = computed((): SupplierInvoiceProjectChangerMode => {
  const shouldNotChangeProject = ['paid', 'approved'].includes(
    supplierInvoice.value.status ?? '',
  );

  if (!permittedTo.value?.edit.project) {
    // Always render it as read only for users with read access
    return 'readonly';
  }

  if (shouldNotChangeProject) {
    return 'disabled';
  }

  return 'select';
});
</script>
<template>
  <div
    v-if="showData"
    class="flex-grid-page"
    :class="{ 'is-full-width': !invoiceFiles }"
  >
    <div class="grid-item">
      <div class="header">
        <h1 class="is-size-4 mb-4 header">
          <span>
            {{ supplierInvoice.supplierInvoiceNumber }} -
            {{ supplierInvoice.supplierName }}
          </span>
          <span class="tag is-medium ml-2" :class="supplierInvoice.status">
            {{ t(supplierInvoice?.status || '') }}
          </span>
        </h1>
      </div>
      <div>
        <supplier-invoice-meta-data
          :supplier-invoice="supplierInvoice"
        ></supplier-invoice-meta-data>
        <SupplierInvoiceProjectChanger
          :mode="projectChangerMode"
          v-model:selected-project-id="supplierInvoiceProjectId"
          :user="currentUser"
        />
      </div>
      <hr />
      <div class="split-invoice">
        <SplitInvoices
          :notes="supplierInvoice.notes ?? ''"
          :rows="supplierInvoice.rows"
          :invoice-id="utils.type.asType<string>(route.params.invoiceId)"
          :project-id="supplierInvoice.project"
          :has-permission-invoice="hasPermissionInvoice"
          :vat="supplierInvoice.vat"
          :disable="{
            notes: !permittedTo?.edit.comments,
            splitInvoice:
              !permittedTo?.edit.invoice ||
              supplierInvoice.status === SupplierInvoiceStatusType.Voided,
          }"
          :hide="{
            notes: !permittedTo?.read.comments,
          }"
          ref="splitInvoicesRef"
          @saved="onSaved"
        ></SplitInvoices>
        <div
          class="reviewers"
          v-if="
            (permittedTo?.read.reviewers || permittedTo?.read.review) &&
            supplierInvoice.status !== SupplierInvoiceStatusType.Voided
          "
        >
          <div class="reviewers-title" v-if="permittedTo?.read.reviewers">
            <h2 class="is-size-4 mb-4">
              {{ t('invoices.details.reviewers') }}
            </h2>
            <button
              class="button is-ghost"
              @click.prevent.stop="toggleEdit"
              data-test="edit-reviewers"
              v-if="permittedTo?.edit.reviewers"
            >
              <PhPencilSimple :size="24" weight="duotone" />
            </button>
          </div>
          <div class="edit-reviewers" v-if="isEditing">
            <reviewers-select
              :allReviewers="allUsers"
              :defaultReviewers="invoiceReviewersRanking"
              @reviewersChanged="updateReviewers"
            />

            <button
              class="button is-primary mt-4 ml-4"
              @click.prevent.stop="submitUpdateReviewers()"
              data-test="save-reviewers"
            >
              {{ t('save') }}
            </button>
          </div>
          <supplier-invoice-review
            :invoiceReviewers="invoiceReviewers"
            :hide="!permittedTo?.edit.review"
            :before-review="saveFormBeforeReview"
          />
        </div>
      </div>
      <div class="business-events">
        <h2 class="is-size-4 mb-4">
          {{ t('invoices.details.businessEvents') }}
          {{ supplierInvoice.supplierName }}
        </h2>

        <SimpleTable
          :columns="supplierInvoiceColumns"
          :data="invoicesFromSupplier"
          :sum-column-keys="[supplierInvoiceAmountKey]"
          :enable-search="true"
          :row-clickable="true"
        >
          <template #tbody="{ data }">
            <tr @click="openInvoice(row.id)" v-for="row in data" :key="row.id">
              <td
                v-for="(column, index) in supplierInvoiceColumns"
                :key="index"
                :class="[
                  column.align ? column.align : '',
                  column.class ? column.class(row[column.key]) : '',
                ]"
              >
                <span
                  :class="['tag', row[column.key]]"
                  v-if="column.key === 'status' && column.format"
                >
                  {{ column.format(row[column.key]) }}
                </span>
                <span v-else>
                  {{
                    column.format
                      ? column.format(row[column.key])
                      : row[column.key]
                  }}
                </span>
              </td>
            </tr>
          </template>
        </SimpleTable>
      </div>
    </div>
    <div class="grid-item" v-if="invoiceFiles">
      <button class="button mb-4" @click="openPopup(invoiceFiles)">
        Öppna popup
      </button>
      <file-viewer :files="invoiceFiles" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'bulma/sass/utilities/initial-variables' as *;

.flex-grid-page {
  display: grid;
  grid-template-columns: 60% 40%;
  column-gap: 4rem;

  &.is-full-width {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: $desktop) {
    grid-template-columns: 1fr;
    row-gap: 2rem;
  }
}
.header {
  display: flex;
  align-items: center;
}

.reviewers {
  margin-top: 2rem;
  &-title {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    h2 {
      margin: 0 !important;
    }
  }
}

.business-events {
  margin-top: 2rem;
}
</style>
